import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export type BillingCustomer = {
  name: string;
  email: string;
  phone: string;
  address: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    postal_code?: string | null;
    country?: string | null;
    state?: string | null;
  } | null;
  tax_id: string;
};

interface Props {
  customer: BillingCustomer | null;
  onSaved: () => void;
}

const BillingDetailsForm = ({ customer, onSaved }: Props) => {
  const [saving, setSaving] = useState(false);
  const initialForm = {
    name: "", phone: "", line1: "", line2: "", city: "",
    postal_code: "", country: "GB", tax_id: "",
  };
  const [form, setForm] = useState(initialForm);
  const [baseline, setBaseline] = useState(initialForm);

  useEffect(() => {
    if (!customer) return;
    const next = {
      name: customer.name || "",
      phone: customer.phone || "",
      line1: customer.address?.line1 || "",
      line2: customer.address?.line2 || "",
      city: customer.address?.city || "",
      postal_code: customer.address?.postal_code || "",
      country: customer.address?.country || "GB",
      tax_id: customer.tax_id || "",
    };
    setForm(next);
    setBaseline(next);
  }, [customer]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const hasChanges = JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke("manage-billing", {
        body: {
          action: "update_billing",
          name: form.name,
          phone: form.phone,
          address: {
            line1: form.line1, line2: form.line2, city: form.city,
            postal_code: form.postal_code, country: form.country?.toUpperCase(),
          },
          tax_id: form.tax_id,
        },
      });
      if (error) throw error;
      toast.success("Billing details updated");
      setBaseline(form);
      onSaved();
    } catch (e: any) {
      toast.error(`Could not save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing details</CardTitle>
        <CardDescription>Used on Stripe invoices and receipts</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Name / Company</Label>
            <Input value={form.name} onChange={set("name")} />
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={set("phone")} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Address line 1</Label>
            <Input value={form.line1} onChange={set("line1")} />
          </div>
          <div className="space-y-2">
            <Label>City</Label>
            <Input value={form.city} onChange={set("city")} />
          </div>
          <div className="space-y-2">
            <Label>Postcode</Label>
            <Input value={form.postal_code} onChange={set("postal_code")} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save billing details"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BillingDetailsForm;
